import React from 'react';
import { chatLogger } from './logger';

/**
 * Performance monitoring utilities for OfferChat.ai
 * Tracks rendering performance and chat metrics
 */

export interface PerformanceMetric {
  name: string;
  value: number;
  unit: 'ms' | 'count' | 'bytes';
  timestamp: number;
  category: 'render' | 'network' | 'memory' | 'user';
}

class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];
  private timers: Map<string, number> = new Map();
  private readonly maxMetrics = 200;

  private addMetric(metric: PerformanceMetric) {
    this.metrics.push(metric);
    
    // Keep metrics array manageable
    if (this.metrics.length > this.maxMetrics) {
      this.metrics.shift();
    }

    // Log significant performance issues
    if (metric.category === 'render' && metric.value > 100) {
      chatLogger.performance(`Slow render detected: ${metric.name}`, {
        duration: metric.value,
        unit: metric.unit
      });
    }
  }

  // Start timing an operation
  startTimer(name: string) {
    this.timers.set(name, performance.now());
  }

  // End timing and record metric
  endTimer(name: string, category: 'render' | 'network' | 'memory' | 'user' = 'render') {
    const startTime = this.timers.get(name);
    if (!startTime) {
      chatLogger.error(`Timer "${name}" was not started`);
      return;
    }

    const duration = performance.now() - startTime;
    this.timers.delete(name);

    this.addMetric({
      name,
      value: duration,
      unit: 'ms',
      timestamp: Date.now(),
      category
    });

    return duration;
  }

  // Record a custom metric
  recordMetric(name: string, value: number, unit: 'ms' | 'count' | 'bytes', category: 'render' | 'network' | 'memory' | 'user') {
    this.addMetric({
      name,
      value,
      unit,
      timestamp: Date.now(),
      category
    });
  }

  // Get metrics for analysis
  getMetrics(category?: string, limit: number = 50): PerformanceMetric[] {
    let filteredMetrics = this.metrics;
    
    if (category) {
      filteredMetrics = this.metrics.filter(m => m.category === category);
    }
    
    return filteredMetrics.slice(-limit);
  }

  // Get performance summary
  getSummary(): {
    averageRenderTime: number;
    slowRenders: number;
    totalMessages: number;
    memoryUsage?: number;
  } {
    const renderMetrics = this.metrics.filter(m => m.category === 'render');
    const messageMetrics = this.metrics.filter(m => m.name.includes('message'));
    
    const averageRenderTime = renderMetrics.length > 0
      ? renderMetrics.reduce((sum, m) => sum + m.value, 0) / renderMetrics.length
      : 0;
    
    const slowRenders = renderMetrics.filter(m => m.value > 100).length;
    
    return {
      averageRenderTime,
      slowRenders,
      totalMessages: messageMetrics.length,
      memoryUsage: this.getMemoryUsage()
    };
  }

  private getMemoryUsage(): number | undefined {
    if ('memory' in performance) {
      // @ts-expect-error - Chrome specific API
      return performance.memory?.usedJSHeapSize;
    }
    return undefined;
  }

  // Clear metrics
  clear() {
    this.metrics = [];
    this.timers.clear();
  }
}

// Create singleton instance
export const performanceMonitor = new PerformanceMonitor();

// React hook for performance monitoring
export function usePerformanceMonitor() {
  const startTimer = (name: string) => performanceMonitor.startTimer(name);
  const endTimer = (name: string, category?: 'render' | 'network' | 'memory' | 'user') => 
    performanceMonitor.endTimer(name, category);
  
  const recordMetric = (name: string, value: number, unit: 'ms' | 'count' | 'bytes', category: 'render' | 'network' | 'memory' | 'user') =>
    performanceMonitor.recordMetric(name, value, unit, category);

  return {
    startTimer,
    endTimer,
    recordMetric,
    getSummary: () => performanceMonitor.getSummary(),
    getMetrics: (category?: string, limit?: number) => performanceMonitor.getMetrics(category, limit)
  };
}

// Higher-order component for automatic performance monitoring
export function withPerformanceMonitoring<P extends object>(
  Component: React.ComponentType<P>,
  componentName: string
) {
  return function MonitoredComponent(props: P) {
    React.useEffect(() => {
      performanceMonitor.startTimer(`${componentName}-mount`);
      
      return () => {
        performanceMonitor.endTimer(`${componentName}-mount`, 'render');
      };
    }, []);

    React.useLayoutEffect(() => {
      performanceMonitor.startTimer(`${componentName}-render`);
      performanceMonitor.endTimer(`${componentName}-render`, 'render');
    });

    return React.createElement(Component, props);
  };
}

// Utility function to measure async operations
export async function measureAsync<T>(
  name: string,
  operation: () => Promise<T>,
  category: 'network' | 'memory' | 'user' = 'network'
): Promise<T> {
  performanceMonitor.startTimer(name);
  try {
    const result = await operation();
    performanceMonitor.endTimer(name, category);
    return result;
  } catch (error) {
    performanceMonitor.endTimer(name, category);
    throw error;
  }
}
